/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-misused-promises */

import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { Layout, Button } from 'tea-component';
import Loading from '@gp/components/Loading';
import NoData from './components/ReportNoData';
import ReportPermissionChecker from './components/ReportPermissionChecker';
import ReviewReportFilter from './components/Filter/ReviewReportFilter';
import ReviewReportV1 from './v1/components/ReviewReport';
import ReviewReportV2 from './v2/components/ReviewReport';
import ReviewReportV3 from './v3/components/ReviewReport';
import usePageLog from '@gp/hooks/usePageLog';
import useImgsLoadingCompleted from '@gp/hooks/useImgsLoadingCompleted';
import useDPSReportMomentLocale from './_hooks/useDPSReportMomentLocale';
import useDPSReports from './_hooks/useDPSReports';
import useDPSReportDownload from './_hooks/useDPSReportDownload';
import { reportOperationLog } from '@gp/services/global';
import {
  getInitialReportId,
  getDPSReportDateTitle,
  getNewCurrentReportIdWhenDeleteCurrentReport,
} from './utils';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { DPS_REPORT_SUB_TITLE } from './constants';

import type { IBaseProps } from '@home/types/common';
import type { IReviewReportFilter } from './components/Filter/ReviewReportFilter';
import type { IDPSReportVOV2 } from '@gp/types/dpsReport/v2';
import type {
  IDPSReportModuleTitles,
  IDPSReportVOV3,
} from '@gp/types/dpsReport/v3';

import './scss/ReviewReport.scss';

interface IReviewReportProps extends IBaseProps {
  hasEditPermission?: boolean;
}

const { Content } = Layout;

const ReviewReport: React.FC<IReviewReportProps> = (props) => {
  const { style, className, hasEditPermission = false } = props;

  usePageLog({ pageId: 'WORKSPACE_DPS_WEEKLY_REPORT' });

  useDPSReportMomentLocale();

  const {
    isLoading,
    isReportLoading,
    report,
    reports,
    reportOptions,
    updateReport,
    onAddReport: handleAddReport,
    onEditReport: handleEditReport,
    onPublishReport: handlePublishReport,
    onDeleteReport: handleDeleteReport,
  } = useDPSReports();

  const isNoData = !Array.isArray(reports) || !reports.length;

  const reportScrollableNode = useRef<HTMLDivElement>(null);

  const [filter, setFilter] = useState<IReviewReportFilter>({ id: '' });

  const dateTitle = useMemo<string>(
    () => getDPSReportDateTitle(report),
    [report],
  );
  const subTitle = dateTitle
    ? `${DPS_REPORT_SUB_TITLE} ${dateTitle}`
    : DPS_REPORT_SUB_TITLE;

  const { node, isDownloading, onDownload } = useDPSReportDownload(
    subTitle,
    report,
  );

  const isReportImgsLoading = useImgsLoadingCompleted(
    node,
    useMemo(() => [isReportLoading, report], [isReportLoading, report]),
  );

  const handleFilterChange = (payload: IReviewReportFilter) => {
    updateReport(payload?.id);
    setFilter({ ...filter, ...payload });
  };

  const handleDeleteReportInPage = async () => {
    if (!report) return;
    try {
      // 这里注意 要先获取删除后，要展示的当前DPS周报的id，因为删除会修改reports数组，所以要在删除前 获取到，然后再执行删除操作
      const idTmp = getNewCurrentReportIdWhenDeleteCurrentReport(
        report?.id,
        reports,
      );
      await handleDeleteReport(report);
      updateReport(idTmp);
      setFilter({ ...filter, id: idTmp });
    } catch (exception) {}
    // @ts-ignore
    reportOperationLog(
      ALL_REPORT_OPERATION_LOG.WORKSPACE_DPS_WEEKLY_REPORT_DELETE_SUBMIT,
      'button',
    );
  };

  const handleEditReportInPage = () => {
    handleEditReport(subTitle);
  };

  const renderReportUI = () => {
    if (isReportLoading)
      return (
        <div style={{ height: 66, color: '#262832' }}>
          <Loading className="dbg-dps-report__state-action" loading />
        </div>
      );

    // GPOP系统维护的可新增、编辑的周报
    if (report?.type === '1')
      return (
        <ReviewReportV2
          node={node}
          dateTitle={dateTitle}
          report={report as IDPSReportVOV2}
        />
      );
    // 早期实现 管理端上传的图片周报
    if (report?.type === '2')
      return (
        <ReviewReportV1
          node={node}
          subTitle={subTitle}
          report={report as IDPSReportVOV2}
        />
      );
    if (report?.type === '3')
      return (
        <ReviewReportV3
          reportScrollableNode={reportScrollableNode}
          node={node}
          dateTitle={dateTitle}
          report={report as IDPSReportVOV3 & IDPSReportModuleTitles}
        />
      );

    return (
      <ReviewReportV2
        node={node}
        dateTitle={dateTitle}
        report={report as IDPSReportVOV2}
      />
    );
  };

  useEffect(() => {
    if (isLoading) return;
    const initialId = getInitialReportId(reports);
    updateReport(initialId);
    setFilter({ ...filter, id: initialId });
  }, [isLoading]);

  return (
    <Content
      style={style}
      className={classnames(
        'dbg-dps-report dbg-dps-report__content',
        className,
      )}
    >
      <Content.Header
        className="dbg-dps-report__header"
        title="Weekly Newsletter"
      />
      <Content.Body
        className="dbg-dps-report__body dbg-dps-report__body-sticky"
        full
        ref={reportScrollableNode}
      >
        {isLoading && (
          <div style={{ height: 66, color: '#262832' }}>
            <Loading className="dbg-dps-report__state-action" loading />
          </div>
        )}
        <div className="dbg-dps-report-sticky">
          {!isLoading && hasEditPermission && (
            <div className="dbg-dps-report__topbar">
              <Button type="primary" onClick={handleAddReport}>
                Add new Newsletter
              </Button>
            </div>
          )}
          {!isLoading && !isNoData && (
            <ReviewReportFilter
              className="dbg-dps-report__filter"
              node={node}
              hasPermission={hasEditPermission}
              // 是否可编辑，GPOP维护的周报可以编辑，管理端上传的周报不能在GPOP编辑
              isEditable={['1', '3'].includes(report?.type as any)}
              isReportImgsLoading={isReportImgsLoading}
              isDownloading={isDownloading}
              reports={reports}
              reportOptions={reportOptions}
              isPublish={report?.is_publish}
              dateTitle={dateTitle}
              subTitle={subTitle}
              report={report}
              value={filter}
              onChange={handleFilterChange}
              onEdit={handleEditReportInPage}
              onPublish={handlePublishReport}
              onDelete={handleDeleteReportInPage}
              onDownload={onDownload}
            />
          )}
        </div>
        {!isLoading && !isNoData && <>{renderReportUI()}</>}
        {!isLoading && isNoData && (
          <NoData hasPermission={hasEditPermission} onAdd={handleAddReport} />
        )}
      </Content.Body>
    </Content>
  );
};

const ReviewReportWrapper: React.FC = () => (
  <ReportPermissionChecker mode="view">
    <ReviewReport />
  </ReportPermissionChecker>
);

export default ReviewReportWrapper;
